// lingland-frontend/src/hooks/useQuoteForm.js (POPRAWIONA WERSJA v2)
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 20;
const MAX_TOTAL_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['doc', 'docx', 'pdf', 'txt', 'jpg', 'jpeg', 'png', 'xlsx', 'xls', 'html', 'xml'];
const POLLING_INTERVAL = 3000;
const MAX_POLLING_ATTEMPTS = 15;

export const useQuoteForm = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ service_type: 'translation', source_language: '', target_language: '', email: '', name: '', notes: '', consent: false });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });
    const [formErrors, setFormErrors] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [fetchedQuoteDetails, setFetchedQuoteDetails] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [selectKey, setSelectKey] = useState(0);

    const pollingIntervalRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const resultsRef = useRef(null); // <-- BRAKUJĄCA DEFINICJA JEST TUTAJ
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    const languageOptions = useMemo(() => languages.map(lang => ({ value: lang.code, label: lang.name })), [languages]);

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/languages`);
                setLanguages(response.data);
            } catch (error) { console.error("useQuoteForm: Error fetching languages:", error); setFormErrors(prev => ({ ...prev, languages: t('quote_form_error_lang_loading') })); }
        };
        fetchLanguages();
    }, [apiUrl, t]);

    const handleInputChange = (e) => { const { name, value, type, checked } = e.target; setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value })); if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null })); };
    const handleSelectChange = (selectedOption, actionMeta) => { const { name } = actionMeta; const value = selectedOption ? selectedOption.value : ''; setFormData(prev => ({ ...prev, [name]: value })); if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null })); };
    
    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) { clearTimeout(pollingIntervalRef.current); pollingIntervalRef.current = null; }
        if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
        setIsPolling(false);
        setProgressPercentage(0);
    }, []);

    const pollForQuoteStatus = useCallback((quoteId) => {
        stopPolling();
        setFetchedQuoteDetails(null);
        setIsPolling(true);
        setProgressPercentage(5);
        progressIntervalRef.current = setInterval(() => setProgressPercentage(prev => prev < 90 ? prev + (90 - prev) * 0.1 : prev), 500);
        const performPoll = async (attempt) => {
            if (!pollingIntervalRef.current && attempt > 1) return;
            if (attempt > MAX_POLLING_ATTEMPTS) { setSubmitStatus({ type: 'info', message: t('quote_form_status_polling_long') }); stopPolling(); return; }
            try {
                const response = await axios.get(`${apiUrl}/api/quotes/${quoteId}`);
                if (response.data.status !== 'pending') {
                    setProgressPercentage(100);
                    setFetchedQuoteDetails(response.data);
                    setSubmitStatus({ type: 'success', message: t('quote_form_status_success') });
                    stopPolling();
                } else if (pollingIntervalRef.current) {
                    pollingIntervalRef.current = setTimeout(() => performPoll(attempt + 1), POLLING_INTERVAL);
                }
            } catch (error) {
                const errorMsg = error.response?.status === 404 ? t('quote_form_status_error_not_found') : t('quote_form_status_error_polling');
                setSubmitStatus({ type: 'error', message: errorMsg });
                stopPolling();
            }
        };
        pollingIntervalRef.current = setTimeout(() => performPoll(1), POLLING_INTERVAL / 2);
    }, [stopPolling, t, apiUrl]);
    
    useEffect(() => {
        if (fetchedQuoteDetails && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [fetchedQuoteDetails]);

    const addFiles = useCallback((newFiles) => {
        if (!newFiles || newFiles.length === 0) return;
        let filesToAdd = Array.from(newFiles); let combinedFiles = [...selectedFiles]; let currentTotalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);
        let localErrors = {};
        filesToAdd.forEach(file => {
            if (combinedFiles.length >= MAX_FILES) { localErrors.files = t('quote_form_error_max_files', { maxFiles: MAX_FILES, fileName: file.name }); return; }
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(extension)) { localErrors[`file_${file.name}`] = t('quote_form_error_file_type', { fileName: file.name }); return; }
            if (file.size > MAX_FILE_SIZE_BYTES) { localErrors[`file_${file.name}`] = t('quote_form_error_file_size', { fileName: file.name, maxFileSize: MAX_FILE_SIZE_MB }); return; }
            if (currentTotalSize + file.size > MAX_TOTAL_SIZE_BYTES) { localErrors.totalSize = t('quote_form_error_total_size', { fileName: file.name, maxTotalSize: MAX_TOTAL_SIZE_MB }); return; }
            combinedFiles.push(file); currentTotalSize += file.size;
        });
        setSelectedFiles(combinedFiles); setSubmitStatus({ type: null, message: '' }); setFormErrors(prev => ({ ...prev, ...localErrors }));
        if (combinedFiles.length > 0 && formErrors.files === t('quote_form_error_no_files')) { const { files, ...rest } = formErrors; setFormErrors(rest); }
        if (currentTotalSize <= MAX_TOTAL_SIZE_BYTES && formErrors.totalSize) { const { totalSize, ...rest } = formErrors; setFormErrors(rest); }
    }, [selectedFiles, formErrors, t]);

    const handleFileChange = (e) => { addFiles(e.target.files); if (e.target) e.target.value = null; };
    const handleRemoveFile = (indexToRemove) => setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
    const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
    const handleDrop = useCallback((e) => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }, [addFiles]);

    const handleSubmit = async (e) => {
        e.preventDefault(); stopPolling(); setFetchedQuoteDetails(null); setSubmitStatus({ type: null, message: '' });
        const errors = {};
        if (!formData.service_type) errors.service_type = t('quote_form_error_service_type');
        if (!formData.source_language) errors.source_language = t('quote_form_error_source_lang');
        if (!formData.target_language) errors.target_language = t('quote_form_error_target_lang');
        else if (formData.source_language === formData.target_language) errors.target_language = t('quote_form_error_different_langs');
        if (selectedFiles.length === 0) errors.files = t('quote_form_error_no_files');
        if (!formData.email) errors.email = t('quote_form_error_email_required');
        else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('quote_form_error_email_invalid');
        if (!formData.consent) errors.consent = t('quote_form_error_consent_required');
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) return;
        setIsSubmitting(true);
        const submissionData = new FormData();
        Object.entries(formData).forEach(([key, value]) => submissionData.append(key, value));
        selectedFiles.forEach(file => submissionData.append('quoteFiles', file, file.name));
        try {
            const response = await axios.post(`${apiUrl}/api/quotes`, submissionData);
            setFormData({ service_type: 'translation', source_language: '', target_language: '', email: '', name: '', notes: '', consent: false });
            setSelectedFiles([]); setFormErrors({}); setSelectKey(prev => prev + 1);
            pollForQuoteStatus(response.data.quoteId);
        } catch (error) {
            // ... logika obsługi błędów serwera ...
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => () => stopPolling(), [stopPolling]);

    return {
        formData, selectedFiles, isSubmitting, submitStatus, formErrors, isDragging,
        isPolling, progressPercentage, fetchedQuoteDetails, languages, languageOptions,
        selectKey, handleInputChange, handleSelectChange, handleFileChange, handleRemoveFile,
        handleDragOver, handleDragLeave, handleDrop, handleSubmit, resultsRef
    };
};